import React from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useCountries } from "@/hooks/useCountries";

export interface EntityAddressFormValues {
  addressSubDwelling: string;
  addressNameNumber: string;
  street: string;
  locality: string;
  town: string;
  county: string;
  postcode: string;
  uprn: string;
  countryCode: string;
}

export const EMPTY_ENTITY_ADDRESS: EntityAddressFormValues = {
  addressSubDwelling: "",
  addressNameNumber: "",
  street: "",
  locality: "",
  town: "",
  county: "",
  postcode: "",
  uprn: "",
  countryCode: "",
};

interface Props {
  values: EntityAddressFormValues;
  onChange: (patch: Partial<EntityAddressFormValues>) => void;
  disabled?: boolean;
}

export const EntityAddressFields: React.FC<Props> = ({ values, onChange, disabled = false }) => {
  const { countryCodeOptions, loading: loadingCountries } = useCountries();

  const setField = <K extends keyof EntityAddressFormValues>(key: K, value: string) => {
    onChange({ [key]: value });
  };

  return (
    <section className="space-y-4 rounded-2xl border border-bridge-border/50 bg-bridge-surface/30 p-4 shadow-sm sm:p-5">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#009BF5]/10 text-[#009BF5]">
          <MapPin className="h-4 w-4" />
        </span>
        <h3 className="text-sm font-semibold text-bridge-fg">Address</h3>
      </div>

      <div className="space-y-4">
        <AddressFormRow>
          <AddressField label="Sub Dwelling">
            <Input
              className="w-full"
              placeholder="Flat, unit, etc."
              value={values.addressSubDwelling}
              onChange={(e) => setField("addressSubDwelling", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
          <AddressField label="Name/Number">
            <Input
              className="w-full"
              placeholder="e.g. 12, 12A, or Rose Cottage"
              value={values.addressNameNumber}
              onChange={(e) => setField("addressNameNumber", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
        </AddressFormRow>

        <AddressFormRow>
          <AddressField label="Street">
            <Input
              className="w-full"
              placeholder="e.g. High Street"
              value={values.street}
              onChange={(e) => setField("street", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
          <AddressField label="Town">
            <Input
              className="w-full"
              placeholder="e.g. London"
              value={values.town}
              onChange={(e) => setField("town", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
        </AddressFormRow>

        <AddressFormRow>
          <AddressField label="Locality">
            <Input
              className="w-full"
              value={values.locality}
              onChange={(e) => setField("locality", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
          <AddressField label="County">
            <Input
              className="w-full"
              value={values.county}
              onChange={(e) => setField("county", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
        </AddressFormRow>

        <AddressFormRow>
          <AddressField label="Postcode">
            <Input
              className="w-full"
              placeholder="e.g. EC3N 4AC"
              value={values.postcode}
              onChange={(e) => setField("postcode", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
          <AddressField label="UPRN">
            <Input
              className="w-full"
              placeholder="Unique Property Reference Number"
              value={values.uprn}
              onChange={(e) => setField("uprn", e.target.value)}
              disabled={disabled}
            />
          </AddressField>
        </AddressFormRow>

        <AddressFormRow>
          <AddressField label="Country Code">
            <MenuSelect
              hideLabel
              value={values.countryCode}
              onChange={(v) => setField("countryCode", v)}
              options={countryCodeOptions}
              placeholder="Select country"
              loading={loadingCountries}
              searchable
              disabled={disabled}
            />
          </AddressField>
        </AddressFormRow>
      </div>
    </section>
  );
};

function AddressFormRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-2 lg:gap-x-10">
      {children}
    </div>
  );
}

function AddressField({
  label,
  children,
  required = false,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <label className="w-[7.25rem] shrink-0 pt-2.5 text-sm text-bridge-muted">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
